// @ts-nocheck TODO

import {html, nothing} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {MobxLitElement} from "@adobe/lit-mobx";
import {classMap} from "lit/directives/class-map.js";

import "./Outline.js";
import "./SidebarOnboardingHelp.js";
import "../common/controls-inputs/IconButton.js";
import "../CreateProjectModal.js";
import {sidebarOutlineStyles} from "./SidebarOutline.styles.js";

import Data from "../../main/data/index.js";
import Bookmark from "../../integrations/highlighter/bookmark.js";
import UI_MODES from "./ui_modes.js";
import {FILE_FORMATS, SUBFORMATS_WORD, SUBFORMATS_EXCEL} from "../../main/constants/outlineExports.js";
import {mergedOutline2} from "../../main/data/model/index.js";
import Integration from "../../integrations/integration/index.js";

@customElement("pn-sidebar-outline")
export class SidebarOutline extends MobxLitElement {
	@property() annotation = {
		type:       "",
		ai:         false,
		renderable: true,
		pageData:   null,
		citation:   null
	};
	@property({type: Boolean}) isEditable = false;
	@property({type: Boolean}) isSidebarApiClosed = true;
	@property({type: Boolean}) showToolTip = false;
	@property({type: Boolean}) disabled = false;
	@property({type: Boolean}) floating = false;
	@property({type: Boolean}) onboardingHelp = true;
	@property() forceOutline:         mergedOutline2[] | false = false;
	@property({type: UI_MODES}) mode: UI_MODES;

	@state() _selectedSort: "topic" | "source" | "date" | "editor" = "topic";
	@state() _selectedView: "all" | "page" | "summary" | "bookmarks" = "all";
	@state() _showExtraOptions = false;

	static styles = [sidebarOutlineStyles];

	onClickExtraOptions = () => {
		this._showExtraOptions = !this._showExtraOptions;
	};

	renderFilterButton() {
		return html`
			<pn-icon-button
				class="viewsbox-icon-wrapper"
				icon="${"filter"}"
				id="btn-extra-options"
				.onClick="${this.onClickExtraOptions}"
				.tippyOptions="${{placement: "bottom"}}"
				.tooltipText="${"Sort and Filter"}"
			></pn-icon-button>
		`;
	}

	renderOutlineLinkButton(url: string) {
		return html`
			<pn-icon-button
				class="viewsbox-icon-wrapper"
				icon="${"settings"}"
				id="btn-outline=link"
				.onClick="${() => window.open(url)}"
				.tippyOptions="${{placement: "bottom"}}"
				.tooltipText="${"Manage Project"}"
			></pn-icon-button>
		`;
	}

	renderBookmarkButton() {
		const bookmarked = !!Data.app.bookmarkUuid;

		const title = this.isEditable
			? (bookmarked ? "Remove Bookmark" : "Add Bookmark")
			: (bookmarked ? "Bookmarked" : "Not Bookmarked");
		const onClick = bookmarked
			? () => Bookmark.unbookmark()
			: () => Bookmark.bookmark();

		const isPqIntegration = Integration.name === "proquest";

		return html`
			<pn-icon-button
				?disabled="${!this.isEditable}"
				icon="${bookmarked ? "bookmark-solid" : "bookmark-outline"}"
				id=${isPqIntegration ? nothing : "btn-bookmark"}
				.isMask="${isPqIntegration ? false : true}"
				.onClick="${this.isEditable ? onClick : nothing}"
				.tippyOptions="${{placement: "bottom"}}"
				.tooltipText="${title}"
			></pn-icon-button>
		`;
	}

	renderSelectedView() {
		const viewOptions = [
			{
				value: "all",
				label: "All"
			},
			/** TODO
			{
				value: "page",
				label: "Current page"
			},
			{
				value: "summary",
				label: "Summary"
			},
			**/
			{
				value: "bookmarks",
				label: "Bookmarks only"
			}
		];

		/** TODO
		if (Utils.Features.isCommentingEnabled()) {
			viewOptions.push({
				value: "comments",
				label: "With comments"
			});
		}
		**/

		const handleChangeOnSidebarView = (newView) => {
			this._selectedView = newView;

			// TODO: Track event with mixpanel.
			// Mixpanel.track("mode change in sidebar", {
			// 	distinct_id: this.store.userData.account.uuid,
			// 	mode:        newView
			// });
		};

		const selectedOption = viewOptions.find(opt => opt.value === this._selectedView);

		return html`
			<pn-select
				.aria-label="${"View mode selection"}"
				id="view-select"
				.onSelectCallback="${handleChangeOnSidebarView}"
				.options="${viewOptions}"
				.selectedOption=${selectedOption}
			>
			</pn-select>
		`;
	}

	renderSelectedSort() {
		const sortOptions = [
			{
				value: "topic",
				label: "Topic"
			},
			{
				value: "date",
				label: "Date"
			},
			{
				value: "source",
				label: "Source"
			},
			{
				value: "editor",
				label: "Editor"
			}
		];

		const handleChangeOnSidebarSort = (newSort) => {
			this._selectedSort = newSort;
		};

		const selectedOption = sortOptions.find(opt => opt.value === this._selectedSort);

		return html`
			<pn-select
				.aria-label="${"Sort by selection"}"
				id="sort-select"
				.onSelectCallback="${handleChangeOnSidebarSort}"
				.options="${sortOptions}"
				.selectedOption=${selectedOption}
			>
			</pn-select>
		`;
	}

	renderExportButton() {
		if (!Data.app.hasBackend)
			return nothing;

		const onExport = async (fileFormat) => {
			const apiUrl = Data.app.config?.urls.pn_api;
			const accessToken = Data.app.accessToken;
			const projectUuid = Data.app.project?.uuid;

			let url = new URL(`${apiUrl}auth-nonce`);
			url.search = new URLSearchParams({access_token: accessToken});

			const data = await fetch(url);
			const auth_nonce = await data.json();

			let params = {};
			switch (fileFormat) {
				case FILE_FORMATS.WORD:
					params = {
						format:               SUBFORMATS_WORD.OUTLINE,
						unapproved_citations: true
					};
					break;

				case FILE_FORMATS.EXCEL:
					params = {
						format: SUBFORMATS_EXCEL.TOPIC_SOURCE
					};
					break;

				case FILE_FORMATS.SOURCES:
					params = {
						unapproved_citations: true
					};
					break;
			}

			url = new URL(`${apiUrl}${fileFormat}/${projectUuid}`);
			url.search = new URLSearchParams({auth_nonce, ...params});

			// Download the file
			window.location.href = url;
		};

		return html`<div class="btn-export-wrapper">
			<pn-dropdown
				icon="download"
				id="btn-export"
				.isDropdown="${true}"
				.tooltipOptions="${{
			position: "bottom",
			text:     "Export"
		}}"
			>
				<pn-dropdown-item .onClick=${() => onExport(FILE_FORMATS.WORD)} type="button">
					Word (.docx)
				</pn-dropdown-item>
				<pn-dropdown-item .onClick=${() => onExport(FILE_FORMATS.EXCEL)} type="button">
					Excel (.xlsx)
				</pn-dropdown-item>
				<pn-dropdown-item .onClick=${() => onExport(FILE_FORMATS.SOURCES)} type="button">
					Sources (.ris)
				</pn-dropdown-item>
			</pn-dropdown>
		</div>`;
	}

	renderTopBar() {
		if (this.forceOutline)
			return nothing;

		const project = Data.app.project;
		const me = Data.app.me;

		const isIntegration = Integration.isIntegration();
		const showOutlineLink = !this.forceOutline && !!(!isIntegration || me.email);
		const outlineLink = showOutlineLink
			? Data.app.getWebAppAuthLink(`project/${project.uuid}/outline`)
			: null;

		const outlineLinkAsButton = ["proquest", "ebsco", "gale"].includes(Integration.name);

		const cssClass = classMap({
			"sidebar-header":     true,
			"show-extra-options": this._showExtraOptions
		});

		return html`<div class=${cssClass}>
			<div class="views-box">
				${outlineLinkAsButton ? nothing : html`
					<div class="outline-link-view">
						${outlineLink
							? html`<a href=${outlineLink} target="_blank">Project Outline</a>`
							: "Project Outline"
						}
					</div>
				`}
				<div class="right-side-icons">
					${this.renderBookmarkButton()}
					${this.renderFilterButton()}
					${this.renderExportButton()}
					${outlineLink && outlineLinkAsButton ? this.renderOutlineLinkButton(outlineLink) : nothing}
				</div>
			</div>

			<div id="extra-options">
				<div class="options-padding"></div>
				<div id="options-container">
					<div class="option">
						<label>Show</label>
						${this.renderSelectedView()}
					</div>
					<div class="option">
						<label>Sort by</label>
						${this.renderSelectedSort()}
					</div>
				</div>
				<div class="options-padding"></div>
			</div>
		</div>`;
	}

	render() {
		const project = Data.app.project;
		const outline = this.forceOutline || Data.app.outline;
		const me = Data.app.me;
		if (outline === undefined || me === undefined || project === undefined)
			return null;

		const isIntegration = Integration.isIntegration();

		const cssClass = classMap({
			"sidebar-outline":                   true,
			["integration-" + Integration.name]: isIntegration
		});

		let showHelp = this.onboardingHelp;
		if (showHelp && this.forceOutline)
			showHelp = false;
		// if (showHelp && outline.find(ot => ot.annotations.length > 0))
		// 	showHelp = false;

		return html`
			<div class=${cssClass}>
				${this.renderTopBar()}

				<div
					class="${classMap({
						"outline-content":         true,
						"is-integration":          isIntegration,
						"is-floating":             this.floating,
						"custom-scroll-bar-light": !isIntegration
					})}"
					id="main-content"
				>
					<pn-outline
						.isEditable=${this.isEditable}
						.mode=${this.mode}
						.selectedView=${this._selectedView}
						.sortBy=${this._selectedSort}
						.forceOutline=${this.forceOutline}
					></pn-outline>

					${showHelp ? html`<pn-sidebar-onboarding-help/>` : nothing}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-sidebar-outline": SidebarOutline;
	}
}
